/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import {
  Box,
  Divider,
  Grid,
  HStack,
  Icon,
  Image,
  Text,
  useDisclosure,
  VStack,
  Tooltip,
} from '@chakra-ui/react';
import { Column } from 'react-table';
import { useGetReturnProducts, useMarkOrderAsReceived } from '@/services/returnOrders';
import { formatDateString } from '@/utils/formatDateString';
import { IReturnProduct } from '@/services/returnOrders/types';
import EyeIcon from '../../../assets/svg/eye.svg?react';
import CustomModal from '@/components/common/CustomModal';
import Field from '@/pages/ProductDetail/Field';
import TickIcon from '../../../assets/svg/tick-circle.svg?react';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';

interface IReturnInProgressProps {
  updateCount: (count: number) => void;
}

const ReturnInProgress: React.FC<IReturnInProgressProps> = ({ updateCount }) => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const { isOpen: isModalOpen, onClose: onModalClose, onOpen: onModalOpen } = useDisclosure();
  const { isOpen: isConfirmOpen, onClose: onConfirmClose, onOpen: onConfirmOpen } = useDisclosure();
  const [returnData, setReturnData] = useState<{
    ProductName: string;
    customerName: string;
    productImage: string;
    refundAmount: number;
    accountName: string;
    accountNumber: string;
    bankBranch: string;
    bankName: string;
    reason: string;
    paymentMethod: string;
    currency: string;
  } | null>(null);
  const [confirmOrderId, setConfirmOrderId] = useState<string | null>(null);

  const [query, setQuery] = useState<string>('');
  const [filters, setFilters] = useState<{
    start_date?: string;
    end_date?: string;
    category?: string;
    subcategory?: string;
  }>({
    start_date: '',
    end_date: '',
    category: '',
    subcategory: '',
  });

  const {
    data: Products,
    refetch,
    isLoading,
    isError,
  } = useGetReturnProducts({
    status: 'inprogress',
    page: pagination.page,
    query,
    ...filters,
  });

  const { mutate: markAsReceived, isLoading: isApproving } = useMarkOrderAsReceived();

  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 10,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
      updateCount(Products.count);
    }
  }, [Products, updateCount]);

  // Cleanup to prevent tooltip persistence
  useEffect(() => {
    return () => {
      setConfirmOrderId(null);
    };
  }, []);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => refetch();

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleApprove = (orderId: string) => {
    setConfirmOrderId(orderId);
    onConfirmOpen();
  };

  const handleConfirmApprove = () => {
    if (!confirmOrderId) return;
    markAsReceived(confirmOrderId, {
      onSuccess: () => {
        refetch();
        onConfirmClose();
      },
      onError: () => {
        onConfirmClose();
      },
    });
  };

  useEffect(() => {
    if (
      filters.start_date === '' &&
      filters.end_date === '' &&
      filters.category === '' &&
      filters.subcategory === ''
    ) {
      refetch();
    }
  }, [filters, pagination.page, refetch]);

  const columns: Column<IReturnProduct>[] = useMemo(
    () => [
      {
        Header: 'Order ID',
        accessor: (row) => row.order_tracking.order_id,
      },
      {
        Header: 'Product Name',
        accessor: (row) => row.order_tracking.product_detail.name,
        Cell: ({ row }: any) => (
          <HStack gap={2} alignItems="center" minWidth="200px">
            <Image
              h="48px"
              w="48px"
              minW="48px"
              maxW="48px"
              maxH="48px"
              borderRadius="lg"
              src={row.original.order_tracking.product_detail.images?.[0]?.image_small}
              alt={row.original.order_tracking.product_detail.name}
              objectFit="cover"
              objectPosition="top"
            />
            <VStack gap={1} align="start" justifyContent="end" h="full">
              <Text>{row.original.order_tracking.product_detail.name}</Text>
            </VStack>
          </HStack>
        ),
      },
      {
        Header: 'Return Address',
        Cell: ({ row }: any) => {
          const pickupAddress = row.original.order_tracking.pickup_address;
          if (!pickupAddress) {
            return <Divider orientation="horizontal" width="20px" />;
          }

          const addressParts = [
            pickupAddress.address_line_1,
            pickupAddress.landmark,
            pickupAddress.town_city,
            pickupAddress.state,
            pickupAddress.pincode,
            pickupAddress.country,
          ].filter(Boolean);

          return <Text minWidth="200px">{addressParts.join(', ')}</Text>;
        },
      },
      {
        Header: 'Refund Amount',
        accessor: (row) => row.refund_amount || '',
        Cell: ({ value, row }: any) =>
          value ? (
            <Text>{`${getCurrencySymbol(row.original.order_tracking.currency)}${value.toFixed(2)}`}</Text>
          ) : (
            <Divider orientation="horizontal" width="20px" />
          ),
      },
      {
        Header: 'Payment',
        accessor: (row) => row.order_tracking.transaction.payment_method,
      },
      {
        Header: 'Order Date',
        Cell: ({ row }: any) => {
          const orderDate = formatDateString(row.original.order_tracking.created_at);
          return <Text minWidth="150px">{orderDate}</Text>;
        },
      },
      {
        Header: 'Return Requested',
        Cell: ({ row }: any) => {
          const returnDate = formatDateString(row.original.returned_at);
          return <Text minWidth="150px">{returnDate}</Text>;
        },
      },
      {
        Header: 'Reason',
        accessor: (row) => row.reason.reason,
        Cell: ({ value }: any) => <Text minWidth="200px">{value}</Text>,
      },
      {
        Header: 'Actions',
        Cell: ({ row }: any) => {
          return (
            <HStack key={row.original.order_tracking.order_id}>
              {row.original.order_tracking.is_order_received === null &&
                row.original.order_tracking.has_delivered === true && (
                  <Tooltip
                    label="Mark as Received"
                    hasArrow
                    placement="bottom"
                    shouldWrapChildren
                    closeOnClick
                    zIndex={1500}
                  >
                    <Icon
                      as={TickIcon}
                      boxSize={5}
                      cursor={isApproving ? 'not-allowed' : 'pointer'}
                      opacity={isApproving ? 0.5 : 1}
                      onClick={() =>
                        !isApproving && handleApprove(row.original.order_tracking.order_id)
                      }
                    />
                  </Tooltip>
                )}
              <Tooltip
                label="View Details"
                hasArrow
                placement="bottom"
                shouldWrapChildren
                closeOnClick
                zIndex={1500}
              >
                <Icon
                  as={EyeIcon}
                  boxSize={5}
                  cursor="pointer"
                  onClick={() => {
                    setReturnData({
                      accountName: row.original.account_number,
                      accountNumber: row.original.account_number,
                      bankBranch: row.original.bank_branch,
                      bankName: row.original.bank_name,
                      customerName: row.original.order_tracking.ship_to.name,
                      paymentMethod: row.original.order_tracking.transaction.payment_method,
                      ProductName: row.original.order_tracking.product_detail.name,
                      productImage: row.original.order_tracking.product_detail.images?.[0]?.image,
                      reason: row.original.reason.reason,
                      refundAmount: row.original.refund_amount,
                      currency: row.original.order_tracking.currency,
                    });
                    onModalOpen();
                  }}
                />
              </Tooltip>
            </HStack>
          );
        },
      },
    ],
    [Products, isApproving],
  );

  return (
    <Box bg="background.white">
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <CustomModal
        title="Return Details"
        isOpen={isModalOpen}
        onClose={onModalClose}
        size="3xl"
        children={
          <>
            <VStack width={'100%'}>
              <HStack gap={6} alignItems={'center'} justifyContent={'start'} width={'100%'}>
                <Image
                  src={returnData?.productImage}
                  alt={returnData?.ProductName}
                  objectFit="cover"
                  objectPosition={'top'}
                  borderRadius={'lg'}
                  boxSize={'100px'}
                />
                <Text
                  fontFamily={'primary'}
                  fontWeight={'semibold'}
                  fontSize={{ md: 'md', sm: 'sm' }}
                  color={'text.primary'}
                >
                  {returnData?.ProductName}
                </Text>
              </HStack>
              <Divider borderColor={'border.default'} />
              <Grid
                fontFamily={'primary'}
                width={'100%'}
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                rowGap={4}
              >
                <Field label="Customer Name" value={returnData?.customerName ?? ''} />
                <Field label="Reason" value={returnData?.reason ?? ''} />
                <Field label="Payment Method" value={returnData?.paymentMethod ?? '-'} />
                <Field
                  label="Refund Amount"
                  value={`${getCurrencySymbol(returnData?.currency)}${returnData?.refundAmount?.toFixed(2) ?? '-'}`}
                />
              </Grid>
              <Divider borderColor={'border.default'} />
              <Grid
                fontFamily={'primary'}
                width={'100%'}
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                rowGap={4}
              >
                <Field label="Bank Name" value={returnData?.bankName ?? ''} />
                <Field label="Branch" value={returnData?.bankBranch ?? ''} />
                <Field label="Account Name" value={returnData?.accountName ?? '-'} />
                <Field label="Account Number" value={returnData?.accountNumber ?? '-'} />
              </Grid>
            </VStack>
          </>
        }
      />
      <CustomConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setConfirmOrderId(null);
          onConfirmClose();
        }}
        onConfirm={handleConfirmApprove}
        icon={TickIcon}
        title="Confirm Mark as Received"
        description="Are you sure you want to mark this return as received? This action will update the return status."
        buttonText="Confirm"
        isLoading={isApproving}
      />
    </Box>
  );
};

export default ReturnInProgress;
/* eslint-disable @typescript-eslint/no-explicit-any */
